import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Helmet } from 'react-helmet'
import { Divider, FormField, FormLoader, Heading, InfoBox, TextField, TextInput } from '../../../../components'
import BankDetails from '../../FormGroups/BankDetails'
import { isEmpty, pathOr } from 'ramda'
import { useSelector } from 'react-redux'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import { apiStateSelectors } from '../../../../../modules/api/apiState'
import { userApiEvents } from '../../../../../modules/api/user'
import { agencyApiEvents } from '../../../../../modules/api/agency'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { vatNumberValidation } from 'utils/yup'
import PhoneField from 'views/components/atoms/PhoneField/PhoneField'
import { $TSFixMe } from 'types/ts-migrate'
import { extractDialCode } from 'utils/formUtils'
import { createPhoneFieldChangeHandler, hasNumberBeyondDialCode } from '../../FormGroups/utils'

type TProps = {
  handleSubmit: (values: $TSFixMe) => void
  initialValues: {
    agencyDetails: {
      companyName: string
      registrationNumber: string
      vatNumber: string
      ffcNumber: string
      firmReferenceNumber: string
      trading: string
    }
    agencyAddress: {
      addressLine1: string
      addressLine2: string
      city: string
      province: string
      postalCode: string
      country: string
    }
    agencyBankDetails: {
      bank: string
      accountNumber: string
      accountType: string
      branchCode: string
      accountName: string
    }
    agencyContactDetails: {
      emailAddress: string
      telephoneNumber: string
    }
    globalVatEnabled: boolean
    isTestAgency: boolean
    integrations: any[]
  }
  isSubmitting: boolean
  isLoading?: boolean
  isValidSupportStaff?: boolean
  currentAgencyId?: string | null
}

const validationSchema = yup.object().shape({
  agencyDetails: yup.object().shape({
    companyName: yup.string().required('required.'),
    registrationNumber: yup
      .string()
      .trim()
      .nullable()
      .matches(/([0-9]{4}\/[0-9]{6}\/[0-9]{2})/, {
        message: 'Invalid format. Must be in the format: ****/******/**',
        excludeEmptyString: true,
      }),
    vatNumber: vatNumberValidation('agencyDetails.vatNumber'),
    firmReferenceNumber: yup.string().nullable(),
  }),
  agencyAddress: yup.object().shape({
    addressLine1: yup.string().required('required.'),
    city: yup.string().required('required.'),
    province: yup.string().required('required.'),
    postalCode: yup.string().required('required.'),
    country: yup.string().required('required.'),
  }),
  agencyBankDetails: yup.object().shape({
    bank: yup.string().required('required.'),
    accountNumber: yup
      .string()
      .required('required.')
      .matches(/^[0-9]*$/, {
        message: 'must be a number.',
        excludeEmptyString: true,
      }),
    accountType: yup.string().required('required.'),
    accountName: yup.string().required('required.'),
  }),
  agencyContactDetails: yup.object().shape({
    emailAddress: yup.string().required('required.').email('Email is invalid.'),
  }),
})

const OnboardingAgencyDetailsForm = ({
  handleSubmit,
  initialValues,
  isSubmitting,
  isLoading,
  currentAgencyId,
  isValidSupportStaff,
}: TProps): React.ReactElement => {
  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  const isFormDisabled = currentAgencyId && !isValidSupportStaff ? true : false

  return (
    <>
      {isFormDisabled && <InfoBox>Please contact support to update business details</InfoBox>}
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          submitCount,
        }) => {
          const submitEvent = !currentAgencyId
            ? userApiEvents.createAgency_request(values)
            : agencyApiEvents.updateAgency_request({ id: currentAgencyId, values })
          const generalErrors = getGeneralFormErrorsByEvent(submitEvent)
          const fieldErrors = getFormFieldErrorsByEvent(submitEvent)
          const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

          // Agencies saved before a field became required load with errors, so
          // only show the error state once a submit has been attempted.
          const formLoaderState = isLoading
            ? 'loading'
            : isSubmitting
            ? 'submitting'
            : (!isEmpty(errors) && submitCount > 0) || hasApiErrors
            ? 'error'
            : undefined

          const getFieldError = (path: any): string => pathOr('', path, touched) && pathOr('', path, errors)

          const telephoneNumber = values.agencyContactDetails?.telephoneNumber
          const telephoneNumberReplaced = telephoneNumber?.replace(/\s/g, '')

          const [telDialCode, setTelDialCode] = useState(() => extractDialCode(telephoneNumber))

          useEffect(() => {
            const newTelDialCode = extractDialCode(telephoneNumber)
            if (telephoneNumber && newTelDialCode !== telDialCode) {
              setTelDialCode(newTelDialCode)
            }
          }, [telephoneNumber])

          return (
            <FormLoader
              onSubmit={handleSubmit}
              state={formLoaderState}
              isDisabled={isFormDisabled}
              buttonProps={{ children: 'Update' }}
            >
              <Helmet>
                <title>reOS | Settings | Business Details</title>
              </Helmet>
              <div>
                <FormErrors errors={generalErrors} />
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.companyName"
                            disabled={isFormDisabled}
                            placeholder="e.g. Gotham Rentals (Pty) Ltd."
                            value={values.agencyDetails.companyName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Entity Name"
                        error={getFieldError(['agencyDetails', 'companyName'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.trading"
                            disabled={isFormDisabled}
                            value={values.agencyDetails.trading}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Trading Name"
                        error={getFieldError(['agencyDetails', 'trading'])}
                      />
                    </FormField>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.registrationNumber"
                            disabled={isFormDisabled}
                            placeholder="e.g. 2018/123456/07"
                            value={values.agencyDetails.registrationNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Entity Registration Number"
                        error={getFieldError(['agencyDetails', 'registrationNumber'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.vatNumber"
                            disabled={isFormDisabled}
                            value={values.agencyDetails.vatNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Entity VAT Number"
                        error={getFieldError(['agencyDetails', 'vatNumber'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.ffcNumber"
                            disabled={isFormDisabled}
                            value={values.agencyDetails.ffcNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="FFC Number"
                        error={getFieldError(['agencyDetails', 'ffcNumber'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyDetails.firmReferenceNumber"
                            disabled={isFormDisabled}
                            value={values.agencyDetails.firmReferenceNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Firm Reference Number"
                        error={getFieldError(['agencyDetails', 'firmReferenceNumber'])}
                      />
                    </FormField>
                  </div>
                </div>

                <Divider>
                  <Heading as="h3">Business Address</Heading>
                  <p>This address will appear on your invoices.</p>
                </Divider>

                <div className="rbn--row">
                  <div className="rbn--col-md-6">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.addressLine1"
                            disabled={isFormDisabled}
                            placeholder="Address line 1"
                            value={values.agencyAddress.addressLine1}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Address"
                        error={getFieldError(['agencyAddress', 'addressLine1'])}
                      />
                    </FormField>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-md-6">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.addressLine2"
                            disabled={isFormDisabled}
                            placeholder="Address line 2"
                            value={values.agencyAddress.addressLine2}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        error={getFieldError(['agencyAddress', 'addressLine2'])}
                      />
                    </FormField>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-md-3">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.city"
                            disabled={isFormDisabled}
                            value={values.agencyAddress.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="City / Municipality"
                        error={getFieldError(['agencyAddress', 'city'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-2">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.province"
                            disabled={isFormDisabled}
                            value={values.agencyAddress.province}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Province"
                        error={getFieldError(['agencyAddress', 'province'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-2">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.postalCode"
                            disabled={isFormDisabled}
                            value={values.agencyAddress.postalCode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Postal Code"
                        error={getFieldError(['agencyAddress', 'postalCode'])}
                      />
                    </FormField>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="agencyAddress.country"
                            disabled={isFormDisabled}
                            value={values.agencyAddress.country}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Country"
                        error={getFieldError(['agencyAddress', 'country'])}
                      />
                    </FormField>
                  </div>
                </div>

                <Divider>
                  <Heading as="h3">Bank Account</Heading>
                </Divider>

                <BankDetails
                  values={values.agencyBankDetails}
                  valueRoot="agencyBankDetails"
                  disableFields={isFormDisabled}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  errors={errors}
                  touched={touched}
                  submitCount={submitCount}
                  requireBankDetails
                />
                <Divider>
                  <Heading as="h3">Contact Details</Heading>
                </Divider>

                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.email}
                            name="agencyContactDetails.emailAddress"
                            disabled={isFormDisabled}
                            value={values.agencyContactDetails?.emailAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label="Business Email Address"
                        error={getFieldError(['agencyContactDetails', 'emailAddress'])}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <TextField
                        inputComponent={
                          <PhoneField
                            defaultCountry="za"
                            value={
                              telephoneNumber?.includes(telDialCode)
                                ? telephoneNumber
                                : [telDialCode, telephoneNumberReplaced].join(' ')
                            }
                            onChange={createPhoneFieldChangeHandler(
                              'agencyContactDetails.telephoneNumber',
                              setFieldValue,
                              setTelDialCode,
                              hasNumberBeyondDialCode,
                            )}
                            disabled={isFormDisabled}
                          />
                        }
                        label="Business Contact Number"
                        error={getFieldError(['agencyContactDetails', 'telephoneNumber'])}
                      />
                    </FormField>
                  </div>
                </div>
              </div>
              <Divider />
            </FormLoader>
          )
        }}
      </Formik>
    </>
  )
}

export default OnboardingAgencyDetailsForm
